import type { TiktokAftersalesItem } from '#/api/tiktok-aftersales';

import dayjs from 'dayjs';

export type AftersalesStageKey =
  | 'ALL'
  | 'BUYER_RETURNED'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'PENDING'
  | 'REJECTED'
  | 'WAIT_BUYER_RETURN';

export const aftersalesStageOptions: Array<{
  key: AftersalesStageKey;
  label: string;
  statuses: string[];
}> = [
  { key: 'ALL', label: '全部', statuses: [] },
  {
    key: 'PENDING',
    label: '待处理',
    statuses: [
      'RETURN_OR_REFUND_REQUEST_PENDING',
      'REPLACEMENT_REQUEST_PENDING',
      'AWAITING_BUYER_RESPONSE',
    ],
  },
  {
    key: 'WAIT_BUYER_RETURN',
    label: '待买家退货',
    statuses: ['AWAITING_BUYER_SHIP', 'RETURN_OR_REFUND_REQUEST_SUCCESS'],
  },
  {
    key: 'BUYER_RETURNED',
    label: '买家已退货',
    statuses: ['BUYER_SHIPPED_ITEM'],
  },
  {
    key: 'COMPLETED',
    label: '已完成',
    statuses: [
      'RETURN_OR_REFUND_REQUEST_COMPLETE',
      'REPLACEMENT_REQUEST_COMPLETE',
    ],
  },
  {
    key: 'REJECTED',
    label: '已拒绝',
    statuses: [
      'REFUND_OR_RETURN_REQUEST_REJECT',
      'REPLACEMENT_REQUEST_REJECT',
      'REJECT_RECEIVE_PACKAGE',
    ],
  },
  {
    key: 'CANCELLED',
    label: '已取消',
    statuses: ['RETURN_OR_REFUND_REQUEST_CANCEL', 'REPLACEMENT_REQUEST_CANCEL'],
  },
];

const TYPE_LABELS: Record<string, string> = {
  REFUND: '仅退款',
  REPLACEMENT: '换货',
  RETURN_AND_REFUND: '退货退款',
};

const STATUS_LABELS: Record<string, string> = {
  AWAITING_BUYER_RESPONSE: '等待买家回复',
  AWAITING_BUYER_SHIP: '等待买家退货',
  BUYER_SHIPPED_ITEM: '买家已退货，待商家收货',
  CLOSED: '已关闭',
  COMPLETED: '已完成',
  REFUND_OR_RETURN_REQUEST_REJECT: '商家已拒绝',
  REJECT_RECEIVE_PACKAGE: '商家拒绝收货',
  REPLACEMENT_REQUEST_CANCEL: '换货申请已取消',
  REPLACEMENT_REQUEST_COMPLETE: '换货已完成',
  REPLACEMENT_REQUEST_PENDING: '换货申请待处理',
  REPLACEMENT_REQUEST_REJECT: '换货申请已拒绝',
  RETURN_OR_REFUND_REQUEST_CANCEL: '售后申请已取消',
  RETURN_OR_REFUND_REQUEST_COMPLETE: '售后已完成',
  RETURN_OR_REFUND_REQUEST_PENDING: '售后申请待商家处理',
  RETURN_OR_REFUND_REQUEST_SUCCESS: '售后申请已通过',
  SELLER_SHIPPED_REPLACEMENT_ITEM: '卖家已发换货商品',
  WAIT_FOR_BUYER_SHIP: '待买家退回',
};

const REASON_LABELS: Record<string, string> = {
  ARRIVED_TOO_LATE: '到货时间太晚',
  BETTER_PRICE_AVAILABLE: '发现更优惠的价格',
  CHANGE_MIND: '买家改变主意',
  DAMAGED: '商品损坏',
  DEFECTIVE: '商品存在质量问题',
  DOES_NOT_FIT: '尺寸不合适',
  ITEM_DID_NOT_ARRIVE: '未收到商品',
  MISSING_PARTS: '商品缺少部件或配件',
  NO_LONGER_NEEDED: '不再需要商品',
  NOT_AS_DESCRIBED: '商品与描述不符',
  ORDER_BY_MISTAKE: '买家误下单',
  OTHER: '其他原因',
  PACKAGE_DAMAGED: '包裹破损',
  QUALITY_NOT_AS_EXPECTED: '商品质量不符合预期',
  RECEIVED_WRONG_ITEM: '收到错误商品',
  SIZE_TOO_LARGE: '尺码太大',
  SIZE_TOO_SMALL: '尺码太小',
  WRONG_ITEM: '收到错误商品',
};

export function returnTypeLabel(value?: string) {
  return value ? TYPE_LABELS[value] || '其他售后类型' : '-';
}

export function returnStatusLabel(value?: string) {
  return value ? STATUS_LABELS[value] || '未知售后状态' : '-';
}

export function returnReasonLabel(value?: string) {
  if (!value) return '未提供原因';
  if (/[\u3400-\u9FFF]/u.test(value)) return value;
  const key = value
    .trim()
    .toUpperCase()
    .replaceAll(/[^A-Z0-9]+/g, '_');
  return REASON_LABELS[key] || '其他原因';
}

export function returnReasonDisplay(row?: Partial<TiktokAftersalesItem>) {
  if (row?.returnReasonText && /[\u3400-\u9FFF]/u.test(row.returnReasonText)) {
    return row.returnReasonText;
  }
  return returnReasonLabel(row?.returnReason || row?.returnReasonText);
}

export function businessStatus(row: Partial<TiktokAftersalesItem>) {
  const status = row.returnStatus || '';
  if (
    status === 'RETURN_OR_REFUND_REQUEST_PENDING' ||
    status === 'REPLACEMENT_REQUEST_PENDING'
  ) {
    return { label: '待处理', type: 'warning' as const };
  }
  if (status === 'AWAITING_BUYER_RESPONSE') {
    return { label: '待买家回复', type: 'warning' as const };
  }
  if (
    status === 'AWAITING_BUYER_SHIP' ||
    (status === 'RETURN_OR_REFUND_REQUEST_SUCCESS' &&
      row.returnType !== 'REFUND')
  ) {
    return { label: '待买家退货', type: 'warning' as const };
  }
  if (status === 'RETURN_OR_REFUND_REQUEST_SUCCESS') {
    return { label: '退款处理中', type: 'warning' as const };
  }
  if (status === 'BUYER_SHIPPED_ITEM') {
    return { label: '买家已退货', type: 'primary' as const };
  }
  if (status.includes('COMPLETE')) {
    return { label: '已完成', type: 'success' as const };
  }
  if (status.includes('REJECT')) {
    return { label: '已拒绝', type: 'danger' as const };
  }
  if (status.includes('CANCEL')) {
    return { label: '已取消', type: 'info' as const };
  }
  return { label: '状态同步中', type: 'info' as const };
}

type MoneyValue = null | number | Record<string, any> | string | undefined;

export function formatAmount(value?: MoneyValue) {
  if (value === undefined || value === null || value === '') return '未返回';
  const normalized =
    typeof value === 'object'
      ? (value.refund_total ?? value.total_refund_amount ?? value)
      : value;
  const amountValue =
    typeof normalized === 'object'
      ? (normalized.amount ?? normalized.value)
      : normalized;
  if (amountValue === undefined || amountValue === null || amountValue === '') {
    return '未返回';
  }
  const amount = Number(amountValue);
  const formatted = Number.isFinite(amount)
    ? amount.toFixed(2)
    : String(amountValue);
  const currency =
    typeof normalized === 'object' ? normalized.currency : undefined;
  return currency ? `${currency} ${formatted}` : `$${formatted}`;
}

export function payoutDisplay(row?: { estimatedPayoutAmount?: MoneyValue }) {
  return {
    amount: formatAmount(row?.estimatedPayoutAmount),
    label: '预估回款',
  };
}

export function refundDisplay(row?: { refundAmount?: MoneyValue }) {
  return formatAmount(row?.refundAmount);
}

export function formatTimestamp(value?: number) {
  return value ? dayjs.unix(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

export function normalizeDeadlineSeconds(deadline?: number) {
  if (!deadline) return undefined;
  return deadline > 10_000_000_000 ? Math.floor(deadline / 1000) : deadline;
}

export function remainingDuration(deadline: number | undefined, now: number) {
  const normalized = normalizeDeadlineSeconds(deadline);
  if (!normalized) return '-';
  const seconds = normalized - now;
  if (seconds <= 0) return '已超时';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainderSeconds = seconds % 60;
  const time = `${String(hours).padStart(2, '0')}小时${String(minutes).padStart(2, '0')}分${String(remainderSeconds).padStart(2, '0')}秒`;
  return days > 0 ? `${days}天${time}` : time;
}
